/**
 * Relationship lines between placed twins for a 2D twin map, on one canvas
 * renderer so thousands of lines stay cheap. Put it inside a `TurboTwinMap`.
 *
 * The host picks the relationships (`relationshipEdges` turns relationship rows
 * into `edges`) and says where each twin sits (`endpointOf`: its marker, or the
 * cluster bubble that holds it). Relationships whose ends meet on the same pair
 * of points draw as one line, with ` ×N` after the name in its tooltip; a dot
 * marks the target end. Lines are dashed, so a link never reads as a twin's
 * geospatial shape (those are solid). A host editor that takes a twin out of the cluster group
 * keeps that twin's lines attached by firing `LIVE_END_EVENT` on the map with
 * `{ twinId, at }` while the twin moves, and `at: null` when the editor closes.
 */
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { escapeHtml } from '../markerIcons';
import {
  LIVE_END_EVENT,
  bundleEdges,
  type LatLngLike,
  type LiveEnd,
  type RelationshipEdge,
} from '../relationshipEdges';

export interface RelationshipLinesLayerProps {
  /** The relationships to draw (see `relationshipEdges`). */
  edges: readonly RelationshipEdge[];
  /** Visible map position of a twin (its cluster bubble when clustered), or null: no line. */
  endpointOf: (twinId: string) => LatLngLike | null;
  /** Change it when markers move or clusters re-form; the lines are redrawn. */
  layoutVersion?: number;
  /** Line colour; the default draws every line in RELATIONSHIP_LINE_COLOR. Keep it stable (not an inline arrow): a new function redraws every line. */
  colorOf?: (edge: RelationshipEdge) => string;
  /** A line click. */
  onEdgeClick?: (sourceTwinId: string, relationshipId: bigint) => void;
  /** A line right-click at viewport (`x`, `y`): every relationship the line draws. */
  onEdgeContextMenu?: (edges: RelationshipEdge[], x: number, y: number) => void;
}

/** The line colour when `colorOf` is not set. */
export const RELATIONSHIP_LINE_COLOR = '#90a4ae';

/** The dash of every relationship line: geospatial shapes draw solid, so the two never look alike. */
const RELATIONSHIP_LINE_DASH = '8 6';

/** The relationship lines as one canvas layer; it draws nothing for an empty `edges`. */
export function RelationshipLinesLayer({
  edges,
  endpointOf,
  layoutVersion,
  colorOf,
  onEdgeClick,
  onEdgeContextMenu,
}: RelationshipLinesLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  // The twin a host editor took out of the cluster group: its end follows the editor (LIVE_END_EVENT).
  const [live, setLive] = useState<LiveEnd | null>(null);
  useEffect(() => {
    const onLive = (e: L.LeafletEvent) => {
      const { twinId, at } = e as unknown as { twinId: string; at: LatLngLike | null };
      setLive((p) => (at ? { twinId, at } : p?.twinId === twinId ? null : p));
    };
    map.on(LIVE_END_EVENT, onLive);
    return () => {
      map.off(LIVE_END_EVENT, onLive);
    };
  }, [map]);

  useEffect(() => {
    const renderer = L.canvas({ padding: 0.5 });
    const layer = L.layerGroup().addTo(map);
    rendererRef.current = renderer;
    layerRef.current = layer;
    return () => {
      layer.remove();
      layerRef.current = null;
      rendererRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const layer = layerRef.current;
    const renderer = rendererRef.current;
    if (!layer || !renderer || !map.hasLayer(layer)) return;
    layer.clearLayers();

    // ponytail: no cap; every relationship of a 10k-twin project puts every bundle on the canvas renderer, cap here if pan/zoom lags
    for (const { a, b, edge, edges: bundled } of bundleEdges(edges, live, endpointOf)) {
      const color = colorOf ? colorOf(edge) : RELATIONSHIP_LINE_COLOR;
      const label = escapeHtml(edge.name) + (bundled.length > 1 ? ` ×${bundled.length}` : '');
      // ponytail: a bundled line's click reports its first relationship; add a picker like the 3D view's merged-line menu if that bites
      L.polyline([a, b], {
        renderer,
        color,
        weight: 2,
        opacity: 0.85,
        dashArray: RELATIONSHIP_LINE_DASH,
        bubblingMouseEvents: false,
      })
        .bindTooltip(label, { sticky: true })
        .on('click', () => onEdgeClick?.(edge.source, edge.id))
        .on('contextmenu', (e: L.LeafletMouseEvent) => {
          L.DomEvent.preventDefault(e.originalEvent);
          onEdgeContextMenu?.(bundled, e.originalEvent.clientX, e.originalEvent.clientY);
        })
        .addTo(layer);
      L.circleMarker(b, {
        renderer,
        radius: 3,
        color,
        fillColor: color,
        fillOpacity: 1,
        interactive: false,
      }).addTo(layer);
    }
  }, [map, edges, endpointOf, live, layoutVersion, colorOf, onEdgeClick, onEdgeContextMenu]);

  return null;
}
